import React, { useEffect, useState, useMemo } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Form, Input, Select, InputNumber, DatePicker } from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { responsive } from "../../utils/style";
import { isAdmin, isStaff, isStudent } from "../../utils/role";
import {
  onEditProfile,
  setIsSuccess,
  onLoadProfile,
  setError,
} from "../../stores/editProfile";
import CompletedModal from "../../components/EditProfile/Modal";
import { parseToMoment } from "../../utils/date";
import { SubmitButton } from "../../components/CustomFields";

import STAR from "../../assets/icons/star.svg";

moment.locale("th");
moment.tz("Asia/Bangkok");

const disabledDate = (current, fields, i) => {
  if (current && moment(current).day() !== 1) return true;
  if (i == 0) {
    return current && current < moment().endOf("day");
  } else {
    const firstStartDate = fields[0].startDate;
    const firstMaxWeek = fields[0].maxWeek;
    const endMatchDate = moment(firstStartDate).add(
      7 * firstMaxWeek - 1,
      "days"
    );
    return current && current < moment(endMatchDate).endOf("day");
  }
};

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSuccess, profile, errorMessage } = useSelector(
    (state) => state.editProfile
  );
  const { role = [] } = useSelector((s) => s.auth.userInfo);
  const [form] = Form.useForm();
  const onFinish = (values) => dispatch(onEditProfile(values));
  const onClose = () => {
    dispatch(setIsSuccess(false));
    navigate("/profile");
  };
  const [, forceUpdate] = useState({});

  const roomUnit = profile.roomUnit || 0;
  const initialValues = useMemo(() => {
    const data = { ...profile, classNo: parseInt(profile.classNo) };
    if (isAdmin(role)) {
      data.matchList = profile.matchList.map((m) => ({
        name: m.name,
        id: m.id,
        startDate: parseToMoment(m.startDate),
        maxWeek: m.maxWeek,
      }));
    }
    return data;
  }, [profile]);

  useEffect(() => {
    dispatch(onLoadProfile());

    // To disable submit button at the beginning.
    forceUpdate({});
  }, []);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues]);

  useEffect(() => {
    errorMessage && alert(errorMessage);
    dispatch(setError(""));
  }, [errorMessage]);

  return (
    <Container>
      <Background>
        <CompletedModal visible={isSuccess} onClose={onClose} />
        <ProfileWrapper>
          <TopicWrapper>
            <Topic>แก้ไขโปรไฟล์</Topic>
            {isAdmin(role) && (
              <Badge>
                <img src={STAR} />
                {` `}
                หัวหน้าโค้ช
              </Badge>
            )}
          </TopicWrapper>
          <SubTopic>{profile.schoolName}</SubTopic>
          <StyleForm
            form={form}
            name="editProfile"
            layout="vertical"
            onFinish={onFinish}
          >
            {isStudent(role) && (
              <>
                <Form.Item
                  name="firstname"
                  label="ชื่อ"
                  required={false}
                  rules={[{ required: true, message: "โปรดระบุ" }]}
                >
                  <Input placeholder="โปรดระบุ" />
                </Form.Item>
                <Form.Item
                  name="nickname"
                  label="ชื่อเล่น"
                  required={false}
                  rules={[{ required: true, message: "โปรดระบุ" }]}
                >
                  <Input placeholder="โปรดระบุ" />
                </Form.Item>
                <Form.Item
                  name="classNo"
                  label="กำลังศึกษาในระดับชั้น"
                  required={false}
                  rules={[{ required: true, message: "โปรดระบุ" }]}
                >
                  <Select placeholder="โปรดระบุ">
                    {[...Array(6).keys()].map((i) => (
                      <Select.Option key={i} value={i + 1}>
                        ม.{i + 1}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="roomNo"
                  label="ห้อง"
                  required={false}
                  rules={[{ required: true, message: "โปรดระบุ" }]}
                >
                  <Select placeholder="โปรดระบุ">
                    {[...Array(roomUnit).keys()].map((i) => (
                      <Select.Option key={i} value={i + 1}>
                        {i + 1}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}
            {isStaff(role) && (
              <>
                <Form.Item
                  name="firstname"
                  label="ชื่อ"
                  required={false}
                  rules={[{ required: true, message: "โปรดระบุ" }]}
                >
                  <Input placeholder="โปรดระบุ" />
                </Form.Item>
              </>
            )}
            {isAdmin(role) && (
              <>
                <Form.Item
                  name="roomUnit"
                  label="จำนวนห้องเรียนสูงสุดของชั้นเรียนทั้งหมด"
                  required={false}
                  rules={[{ required: true, message: "โปรดระบุ" }]}
                >
                  <InputNumber
                    min={1}
                    max={30}
                    step={1}
                    placeholder="โปรดระบุ"
                  />
                </Form.Item>
                <Form.List name="matchList">
                  {(fields) => (
                    <div>
                      {fields.map((field, i) => (
                        <div key={i}>
                          <Form.Item
                            name={[field.name, "name"]}
                            label={
                              i === 0
                                ? "ชื่อกิจกรรมในการแข่งขัน"
                                : `ชื่อกิจกรรมในการแข่งขันของเทอม ${i + 1}`
                            }
                            required={false}
                            rules={[{ required: true, message: "โปรดระบุ" }]}
                          >
                            <Input
                              placeholder="โปรดระบุ"
                              disabled={
                                profile.matchList[i].status !== "created"
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, "startDate"]}
                            label={
                              i === 0
                                ? "วันเริ่มต้นของการแข่งขัน"
                                : `วันเริ่มการแข่งขันของเทอม ${i + 1}`
                            }
                            required={false}
                            rules={[{ required: true, message: "โปรดระบุ" }]}
                          >
                            <DatePicker
                              placeholder="โปรดระบุ"
                              disabledDate={(current) =>
                                disabledDate(
                                  current,
                                  form.getFieldValue("matchList"),
                                  i
                                )
                              }
                              disabled={
                                profile.matchList[i].status !== "created"
                              }
                            />
                          </Form.Item>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.List>
              </>
            )}
            <Form.Item shouldUpdate>
              {() => (
                <SubmitWrapper>
                  <SubmitButton
                    htmlType="submit"
                    disabled={
                      !!form
                        .getFieldsError()
                        .filter(({ errors }) => errors.length).length
                    }
                  >
                    บันทึก
                  </SubmitButton>
                </SubmitWrapper>
              )}
            </Form.Item>
          </StyleForm>
        </ProfileWrapper>
      </Background>
    </Container>
  );
};

export default Profile;

const Container = styled.div`
  font-family: Noto Sans Thai, Noto Sans;
  background-color: #fffbeb;
`;
const Background = styled.div`
  min-height: 100vh;
  max-width: 1144px;
  background-color: white;
  margin: auto;
  padding: 40px 0;

  ${responsive.tablet(css`
    max-width: unset;
  `)}
  ${responsive.mobile(css`
    padding: 40px 16px;
  `)}
`;
const ProfileWrapper = styled.div`
  max-width: 440px;
  margin: auto;
`;
const Topic = styled.h4`
  font-weight: 600;
  font-size: 23px;
  line-height: 28px;
  margin: 0;
  align-self: flex-end;
`;
const SubTopic = styled.h5`
  font-weight: 500;
  font-size: 21px;
  line-height: 23px;
  letter-spacing: -0.02em;
  color: #058459;
  margin-bottom: 16px;
  text-align: center;
`;
const StyleForm = styled(Form)`
  margin-top: 40px;

  .ant-input-number {
    width: 100%;
  }
  input {
    border-radius: 4px;
    height: 52px;
    font-size: 18px;
  }
  .ant-input-affix-wrapper {
    font-size: 18px;
  }
  .ant-select-selector {
    height: 52px !important;
    font-size: 18px;
  }
  .ant-select-selection-placeholder,
  .ant-select-selection-item {
    line-height: 52px !important;
    font-size: 18px;
  }
  .ant-picker {
    width: 100%;
    padding-top: 0;
    padding-bottom: 0;
  }
  .ant-picker-input {
    padding-bottom: 0 !important;
  }
`;
const SubmitWrapper = styled.div`
  display: flex;
  text-align: center;
  justify-content: center;
  margin-top: 16px;
`;
const Badge = styled.div`
  padding: 4px 16px;
  width: 135px;
  height: 34px;
  background: #058459;
  border-radius: 8px;
  font-weight: 700;
  font-size: 16px;
  line-height: 26px;
  color: #ffffff;

  img {
    margin-right: 5px;
  }
`;
const TopicWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
`;
